import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Button,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";

import { useRequestState, useResponsive } from "@app/hooks";
import { FormProvider, RHFToggleSwitch } from "@app/components";
import { Strings } from "@app/constants";

import { BuyCarForm } from "./buy-cars-form";
import { SellCarForm } from "./sell-cars-form";
import { brands, models, sellingCarsData } from "../../categoriesData";
import { BuyAndSellCarsOnlineProps } from "../../types";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { selectBrandState } from "@app/modules/categories/selector";
import { useDispatch } from "react-redux";
import { brand } from "@app/modules/categories/slice";
import { InvestorForm } from "./investor-form";

const BuyAndSellCarsOnline = ({ open, onClose }: BuyAndSellCarsOnlineProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const dispatch = useDispatch();

  const methods = useForm();

  const [mode, setMode] = useState<"buy" | "sell" | "invest">("buy");
  const [formValues, setFormValues] = useState({});
  const navigate = useNavigate();
  const { data: brandData } = useRequestState({
    stateSelector: selectBrandState,
  });

  const handleFormValueChange = useCallback((values: any) => {
    setFormValues(values);
  }, []);

  useEffect(() => {
    dispatch(brand());
  }, [dispatch]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "55%",
          maxWidth: isMobile ? "100%" : "90%",
          borderRadius: isMobile ? 0 : 3,
          p: isMobile ? 2 : 0,
        },
      }}
    >
      <Stack
        sx={{
          maxHeight: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack spacing={3} sx={{ p: isMobile ? 2 : 4 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center">
              {/* <Icon
                icon="mdi-light:arrow-left"
                width={isMobile ? 20 : 24}
                height={isMobile ? 20 : 24}
                color={theme.palette.common.black}
              /> */}
              <Typography
                fontWeight={600}
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  ml: 1,
                  fontFamily: "Work Sans",
                }}
              >
                {Strings.authPopup.sellBuyCars}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size={isMobile ? "small" : "medium"}>
              <Icon
                icon="material-symbols-light:close"
                width={isMobile ? 20 : 24}
                height={isMobile ? 20 : 24}
                color={theme.palette.common.black}
              />
            </IconButton>
          </Stack>

          <FormProvider methods={methods} onSubmit={() => {}}>
            <Stack sx={{ alignItems: "center", py: 1 }}>
              <RHFToggleSwitch
                options={[
                  { label: Strings.authPopup.burCars, value: "buy" },
                  { label: Strings.authPopup.sellCars, value: "sell" },
                  { label: Strings.authPopup.beAnInvestor, value: "invest" },
                ]}
                defaultValue="buy"
                onChange={(val) => setMode(val as "buy" | "sell")}
                width={isMobile ? "100%" : "60%"}
                height={isMobile ? 36 : 49}
              />
            </Stack>

            {mode === "buy" ? (
              <BuyCarForm
                brands={brandData?.map((brands: { name: any }) => brands.name)}
                brandData={brandData}
                onFormValueChange={handleFormValueChange}
                onClose={onClose}
              />
            ) : mode === "invest" ? (
              <InvestorForm
                onFormValueChange={handleFormValueChange}
                onClose={onClose}
              />
            ) : (
              <SellCarForm methods={methods} onClose={onClose} />
            )}
          </FormProvider>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { BuyAndSellCarsOnline };
